import { useMemo, useState } from "react";

import { Chip, IconButton, Tooltip, Typography } from "@material-ui/core";
import { green, orange, red } from "@material-ui/core/colors";

import { User } from "../../../graphql/models";
import { UserPrivacyHistoryWithDialogButton } from "../user_privacy_history/user_privacy_history";
import { PrivacyTip } from "@mui/icons-material";

type UserWithPrivacyPolicy = Pick<User, "id" | "latestUserPrivacySettings">;

export type UserPrivacyStatusProps = {
  user: UserWithPrivacyPolicy;
};
export default function UserPrivacyStatus(props: UserPrivacyStatusProps) {
  const { user } = props;

  const allPoliciesAccepted = useMemo(() => {
    const accepted = [
      user.latestUserPrivacySettings?.basicPolicyAccepted,
      user.latestUserPrivacySettings?.newsletterAccepted,
      user.latestUserPrivacySettings?.thirdPartyDataTransmissionAccepted,
      user.latestUserPrivacySettings?.userPersonalDataTransmissionAccepted,
    ].every((v) => v === true);

    return accepted;
  }, [
    user.latestUserPrivacySettings?.basicPolicyAccepted,
    user.latestUserPrivacySettings?.newsletterAccepted,
    user.latestUserPrivacySettings?.thirdPartyDataTransmissionAccepted,
    user.latestUserPrivacySettings?.userPersonalDataTransmissionAccepted,
  ]);

  const basicPolicyNotAccepted = useMemo(() => {
    return !user.latestUserPrivacySettings?.basicPolicyAccepted;
  }, [user.latestUserPrivacySettings?.basicPolicyAccepted]);

  const chipStyle = useMemo(() => {
    let backgroundColor: string;

    if (allPoliciesAccepted) {
      backgroundColor = green[900];
    } else if (basicPolicyNotAccepted) {
      backgroundColor = red[500];
    } else {
      backgroundColor = orange[500];
    }

    return {
      backgroundColor,
    };
  }, [allPoliciesAccepted, basicPolicyNotAccepted]);

  const label = useMemo(() => {
    if (allPoliciesAccepted) {
      return "Privacy policy interamente accettata";
    } else if (basicPolicyNotAccepted) {
      return "Nessuna policy accettata";
    } else {
      return "Non tutte le policies sono state accettate";
    }
  }, [allPoliciesAccepted, basicPolicyNotAccepted]);

  const tooltip = useMemo(() => {
    return (
      <Typography variant={"body1"}>
        <ul>
          <li>
            Policy base:{" "}
            {user.latestUserPrivacySettings?.basicPolicyAccepted ? "Sì" : "No"}
          </li>
          <li>
            Newsletter:{" "}
            {user.latestUserPrivacySettings?.newsletterAccepted ? "Sì" : "No"}
          </li>
          <li>
            Dati a terzi:{" "}
            {user.latestUserPrivacySettings?.thirdPartyDataTransmissionAccepted
              ? "Sì"
              : "No"}
          </li>
          <li>
            Dati personali:{" "}
            {user.latestUserPrivacySettings
              ?.userPersonalDataTransmissionAccepted
              ? "Sì"
              : "No"}
          </li>
        </ul>
        <br />
        <UserPrivacyHistoryWithDialogButton userId={user.id} />
      </Typography>
    );
  }, [user]);

  return (
    <Tooltip title={tooltip}>
      <IconButton aria-label="privacy" {...props}>
        <PrivacyTip fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}
